import { IconCollection } from "components/Icon/IconCollection";
import InfoPanel from "components/Layout/InfoPanel";
import Placeholder from "components/Layout/Placeholder";
import RecordLabel from "components/RecordLabel";
import RecordTable from "components/Table/RecordTable";
import { TableColumn } from "components/Table/tableUtils";
import { useResourceData } from "containers/Resources/screens/ResourcesSingle/useResourceData";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useMount } from "react-use";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { getRelativeUrl } from "utils/service";
import { sortAlphabetically } from "utils/sorting";
import { getSearchObj } from "utils/utils";
import "./AccessList.scss";

export interface AccessRoleRow {
    id: string;
    name: string;
}

// Roles per page in the access popover. These lists are short; one page fits
// the popover on a normal screen (Margot: there is no room for more rows).
const PAGE_SIZE = 10;
// Row height of a single role row, in px. KEEP IN SYNC with the
// `.recordTableCellClip { height }` rule in AccessList.scss — it is used to
// reserve a stable one-page body height while paginating (see below).
const ACCESS_ROW_PX = 40;

/**
 * Derive the de-duplicated, alphabetically-sorted list of roles that have
 * access, from the raw `role_permission_plain_list` rows. A role can appear in
 * several permission rows; we want one row per role. Pure (no React / store) so
 * it is unit-testable independently of the component.
 */
export const deriveAccessRoleRows = (
    permissionList: any[] | undefined | null
): AccessRoleRow[] => {
    const byRole =
        permissionList?.reduce((acc: Record<string, AccessRoleRow>, currentPermission: any) => {
            acc[currentPermission.role_id] = {
                id: currentPermission.role_id,
                name: currentPermission.role_id_role?.name,
            };
            return acc;
        }, {}) ?? {};
    return sortAlphabetically([...Object.values(byRole)], (role: AccessRoleRow) => role.name);
};

export const AccessList = () => {
    const dispatch = useDispatch();
    const { isFetching, resourceName, recordId } = useResourceData();

    const [isLoading, setIsLoading] = useState(false);
    const [roleRows, setRoleRows] = useState<AccessRoleRow[]>([]);

    const isProject = useMemo(() => resourceName === "project", [resourceName]);
    const isBundle = useMemo(() => resourceName === "bundle", [resourceName]);
    const isWorkspace = useMemo(() => resourceName === "workspace", [resourceName]);

    const fetchRolePermissions = useCallback(() => {
        if (!isProject && !isBundle && !isWorkspace) return;

        setIsLoading(true);

        return new Promise((resolve, reject) => {
            const allProjectsField = getSearchObj({ field: "project_id", op: "eq", value: null });
            const currentProjectField = getSearchObj({
                field: "project_id",
                op: "eq",
                value: recordId,
            });
            const readOperationField = getSearchObj({
                field: "operation",
                op: "eq",
                value: "READ",
            });
            const bundleTypeField = getSearchObj({
                field: "table_type",
                op: "eq",
                value: "BUNDLE",
            });
            const allOperationField = getSearchObj({
                field: "operation",
                op: "eq",
                value: null,
            });
            const allTypeField = getSearchObj({ field: "table_type", op: "eq", value: null });

            const filter = isWorkspace
                ? [allProjectsField]
                : isProject
                ? [{ or: [allProjectsField, currentProjectField] }]
                : [
                      allProjectsField,
                      {
                          and: [
                              {
                                  or: [
                                      { and: [readOperationField, bundleTypeField] },
                                      {
                                          and: [
                                              allTypeField,
                                              { or: [readOperationField, allOperationField] },
                                          ],
                                      },
                                  ],
                              },
                          ],
                      },
                  ];

            const filterStr = JSON.stringify({
                and: filter,
            });
            dispatch({
                type: dataStoreAction.START_FETCH_DATA,
                promise: { resolve, reject },
                key: "rolePermissionList",
                params: {
                    query: `query ($filter: String, $offset: Int, $order: String, $limit: Int){
                            role_permission_plain_list(filter: $filter, offset: $offset, order: $order, limit: $limit)  {
                                project_id
                                role_id
                                role_id_role {
                                    name
                                }
                            }
                        }`,
                    variables: {
                        offset: 0,
                        order: "+id",
                        limit: 500,
                        filter: filterStr,
                    },
                },
            });
        })
            .then((data: any) => {
                setRoleRows(deriveAccessRoleRows(data?.role_permission_plain_list));
            })
            .catch((error) => error)
            .finally(() => setIsLoading(false));
    }, [dispatch, isBundle, isProject, isWorkspace, recordId]);

    useMount(() => fetchRolePermissions());

    const columns: TableColumn[] = useMemo(
        () => [
            {
                label: "Role",
                key: "name",
                shown: true,
                sortable: false,
                dto: (role: AccessRoleRow) => (
                    <RecordLabel
                        key={role.id}
                        link={getRelativeUrl("role", role.id)}
                        name={role.name}
                        icon={IconCollection.badge}
                    />
                ),
            },
        ],
        []
    );

    return (
        <InfoPanel>
            <InfoPanel.Title>{`Roles with access to the current ${resourceName}`}</InfoPanel.Title>
            {/* Fixed width keeps the popover from jiggling horizontally as the
                placeholder is swapped for the loaded table. The accessRoleList
                class drops the shared table's 32px horizontal inset so the role
                column header aligns with the modal title and the pagination
                footer fits without its own scrollbar (see AccessList.scss). */}
            <div className="accessRoleList" style={{ width: 420 }}>
                {isFetching || isLoading ? (
                    <Placeholder.List />
                ) : (
                    <RecordTable
                        columns={columns}
                        data={roleRows}
                        totalItemCount={roleRows.length}
                        recordTypes="all"
                        isLoading={false}
                        tableSettingsKey="accessRoleList"
                        options={{
                            toolbar: "none",
                            toolbarFilters: false,
                            selectableRows: "none",
                            showSearch: false,
                            viewColumns: false,
                            // The "jump to page" input + its label overflow the
                            // fixed popover width, which pushed the footer into its
                            // own horizontal scrollbar with mis-aligned controls.
                            // These access lists are short, so the jump control is
                            // not needed; dropping it lets the remaining footer
                            // controls (rows-per-page + prev/next) fit (Margot).
                            jumpToPage: false,
                            // Compact rows so the default page of role names fits
                            // without wasting vertical space; a role name is a
                            // single line, so the regular row height is excessive.
                            density: "high",
                            // Option (2), Margot's pick: when the list spans more
                            // than one page, reserve a stable body height equal to
                            // exactly one full page, so paging to a shorter last
                            // page does not shrink the popover and move the
                            // pagination control you just clicked. A single-page
                            // list (<= PAGE_SIZE roles) gets no fixed height, so it
                            // stays compact instead of padding out to a full page.
                            ...(roleRows.length > PAGE_SIZE
                                ? { tableBodyHeight: `${PAGE_SIZE * ACCESS_ROW_PX}px` }
                                : {}),
                        }}
                    />
                )}
            </div>
        </InfoPanel>
    );
};
